"use client";

import { Image } from "antd";
import { useState } from "react";
import { LeftOutlined, RightOutlined } from "@ant-design/icons";
import AntdStyleRegistry from "@/app/AntdRegistry";

interface ImageCarouselProps {
    images: string;
    className?: string;
}

export function ImageCarousel({ images, className = "" }: ImageCarouselProps) {
    const imageList = images.split(",").map((url) => url.trim()).filter(Boolean);
    const [currentIndex, setCurrentIndex] = useState(0);

    if (imageList.length === 0) {
        return null;
    }

    const goToPrevious = () => {
        setCurrentIndex((prev) => (prev === 0 ? imageList.length - 1 : prev - 1));
    };

    const goToNext = () => {
        setCurrentIndex((prev) => (prev === imageList.length - 1 ? 0 : prev + 1));
    };

    const goToSlide = (index: number) => {
        setCurrentIndex(index);
    };

    return (
        <AntdStyleRegistry>
        <div className={`relative flex flex-col gap-y-3 lg:gap-y-1 items-center justify-center w-full h-full ${className}`}>
            {/* Main Image Container */}
            <div className="relative flex items-center justify-center w-full h-[calc(100%-3rem)]">
                {/* Left Arrow */}
                <button
                    onClick={goToPrevious}
                    className="absolute left-2 z-10 flex items-center justify-center w-10 h-10 bg-black/50 hover:bg-black/70 text-white rounded-full transition-all duration-200 cursor-pointer"
                    aria-label="Previous image"
                >
                    <LeftOutlined className="text-lg" />
                </button>

                {/* Image */}
                <div className="flex items-center justify-center w-full h-full overflow-hidden">
                    <Image.PreviewGroup items={imageList}>
                        <Image
                            sizes="(max-width: 640px) 60vw, (max-width: 1024px) 50vw, (max-width: 1280px) 33vw, 25vw"
                            srcSet={`
                                ${imageList[currentIndex]}?width=200 400w,
                                ${imageList[currentIndex]}?width=300 600w,
                                ${imageList[currentIndex]}?width=400 800w,
                            `}
                            alt={`Gallery image ${currentIndex + 1}`}
                            style={{
                                maxHeight: "100%",
                                maxWidth: "100%",
                                objectFit: "contain",
                                width: "auto",
                                height: "auto"
                            }}
                            preview={{
                                src: imageList[currentIndex],
                                mask: "Click to view full size",
                                destroyOnClose: true,
                            }}
                        />
                    </Image.PreviewGroup>
                </div>

                {/* Right Arrow */}
                <button
                    onClick={goToNext}
                    className="absolute right-2 z-10 flex items-center justify-center w-10 h-10 bg-black/50 hover:bg-black/70 text-white rounded-full transition-all duration-200 cursor-pointer"
                    aria-label="Next image"
                >
                    <RightOutlined className="text-lg" />
                </button>
            </div>

            {/* Dots Indicator */}
            <div className="flex items-center justify-center gap-2 h-12 shrink-0 flex-wrap">
                {imageList.map((_, index) => (
                    <button
                        key={index}
                        onClick={() => goToSlide(index)}
                        className={`w-3 h-3 rounded-full transition-all duration-200 cursor-pointer ${index === currentIndex
                                ? "bg-gyellow scale-110"
                                : "bg-gray-400 hover:bg-gray-300"
                            }`}
                        aria-label={`Go to image ${index + 1}`}
                    />
                ))}
            </div>
        </div>
        </AntdStyleRegistry>
    );
}
